import {
  useQuery,
  UseQueryOptions,
  QueryKey,
  useMutation,
  UseQueryResult,
} from "@tanstack/react-query";
import getBITSEndApiMap from "../apiMap";
import { api } from "../services/AxiosService";
import { ROLES } from "../constant/roles";
interface UpdateProjectPayload {
  projectId: string;
  title?: string;
  description?: string;
  template_category?: string;
  template_subcategory?: string;
  difficulty_level?: string;
  estimated_hours?: number;
  total_points?: number;
  learning_objectives?: string[];
  prerequisites?: string[];
  skills_covered?: string[];
  technologies_used?: string[];
  tags?: string[];
  id?: string;
}

export interface UpdateProjectResponse {
  isSuccess: boolean;
  message?: string;
  data?: any;
}

// Helper function to replace path params like :projectId, :kernelId, etc.
function buildUrl(template: string, params: Record<string, string | number>) {
  return template.replace(/:([a-zA-Z0-9_]+)/g, (_, key) =>
    String(params[key] ?? ":" + key),
  );
}

export type Difficulty = "beginner" | "intermediate" | "advanced";

export type ProjectType = "group" | "individual";

type dataset = {
  id: string;
  name: string;
  file: File;
  size: string;
};

export type CheckpointRubric = {
  name?: string;
  description: string;
  points: number;
  total_points?: number;
  criteria?: Array<{
    name: string;
    description: string;
    points: number;
  }>;
  _inputPoints?: string;
};

export type checkpoints = {
  id?: string; // Optional ID for existing checkpoints
  project_id?: string;
  isScreen: number;
  title: string;
  description?: string;
  checkpoint_number: number;

  start_date: string;
  due_date: string;
  weight_percentage: number;
  is_required: boolean;
  status: string;
  metadata: {};
  rubrics?: CheckpointRubric[];
};

export type GradingRubric = {
  project_id: string;
  title: string;
  description: string;
  checkpoint: string;
  criteria?: Array<{
    name: string;
    description: string;
    points: number;
  }>;

  total_points: number;
  grading_scale: {};
  is_template: boolean;
  template_name: string;
  checkpoint_mapping: {};
  metadata: {};
};

export interface ProjectData {
  // Step 1: Project Details
  id: string;
  title: string;
  teachingAssistants?: any[];
  // categoryId: string;
  courseId: string;
  description: string;
  instructorIds: string[];
  teachingAssId: string[];
  saveAsTemplate: boolean;
  templateCategory: string;
  templateSubCategory: string;
  projectType: ProjectType;
  difficulty_level: Difficulty;
  totalPoints: number;
  total_points?: number;
  estimatedHours: string;
  tags: string[];
  project_overview: string;
  learning_objectives: string;
  prerequisites: string;
  isScreen: number;
  sandbox_time_duration: string;
  dueDate: string;
  start_date?: string;
  isStep?: number;
  resource_profile_id?: string;
  // Step 2: Assets & Instructions
  projectId: string;
  instructions: string;

  datasets: dataset[];
  dataset_s3_url?: any[]; // Existing datasets from S3
  selectedDatasetId?: string; // Currently selected dataset ID (deprecated - use selectedDatasetIds)
  selectedDatasetIds?: string[]; // Multiple selected dataset IDs (for multi-select)
  originalLinkedDatasetId?: string; // Original dataset ID from server (for tracking changes - deprecated)
  originalLinkedDatasetIds?: string[]; // Original dataset IDs from server (for tracking changes)
  filesToDelete?: string[]; // S3 keys of files marked for deletion
  // Step 3: Review Checkpoints
  checkpoints: checkpoints[];

  // Step 4: Project Settings
  sandboxResourceProfile: string;
  maxSubmissions: number;
  max_attempts?: number;
  acceptForDays: number;
  lateSubmissionsAllowed: boolean;
  auto_grading_enabled?: boolean;
  evaluation_type?: string;
  late_submission_allowed?: boolean;
  late_submission_days_allowed?: number;
  gradingRubric: GradingRubric[];
}
export interface TemplateData {
  // Step 1: Project Details
  id?: string;
  projectId?: string;
  title: string;
  teachingAssistants?: any[];
  courseId?: string;
  description: string;
  instructorIds?: string[];
  teachingAssId?: string[];
  saveAsTemplate?: boolean;
  templateCategory: string;
  templateSubCategory?: string;
  templateSubcategory?: string; // From JSON example
  projectType: ProjectType;
  difficulty_level: Difficulty;
  totalPoints: number;
  estimatedHours: string;
  estimated_hours?: number; // From JSON example
  total_points?: number; // From JSON example
  tags: string[];
  project_overview: string;
  learning_objectives: string;
  prerequisites: string;
  isScreen: number;
  sandbox_time_duration?: string;
  dueDate?: string;
  isStep?: number;
  isTemplate?: boolean; // From JSON example

  // Additional fields from JSON example
  skills_covered?: string[];
  technologies_used?: string[];

  // Step 2: Assets & Instructions
  instructions: string;
  datasets?: dataset[];

  // Step 3: Review Checkpoints
  checkpoints?: checkpoints[];

  // Step 4: Project Settings
  sandboxResourceProfile?: string;
  maxSubmissions: number;
  acceptForDays?: number;
  lateSubmissionsAllowed: boolean;
  gradingRubric?: GradingRubric[];
}

export interface CreateProjectResponse {
  isSuccess: boolean;
  message: string;
  description?: string;
  title?: string;
  projectId?: string;
  data: {
    id: string;
    projectId: string;
    title: string;
    description?: string;
    status: string;
    courseId: string;
    totalPoints: number;
    createdAt: string;
    isScreen: number;
  };
}

export interface UploadProjectDatasetResponse {
  isSuccess: boolean;
  message: string;
  data: DatasetFile[];
}

export interface DatasetFile {
  url: string;
  key: string;
  bucket: string;
  size: number;
  contentType: string;
  etag: string;
  downloadUrl: DownloadUrl;
  isPublic: boolean;
  fileName: string;
}

export interface DownloadUrl {
  presignedUrl: string;
  key: string;
}

export interface RubricCriteria {
  name: string;
  points: number;
  description: string;
}

export interface ProjectBrief {
  id: string;
  title: string;
  description: string;
  status: string;
}

// Main rubric item
export interface RubricItem {
  id: string;
  project_id: string;
  title: string;
  description: string;
  criteria: RubricCriteria[];
  total_points: string;
  grading_scale: {};
  is_template: boolean;
  template_name: string | null;
  created_by: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  project: ProjectBrief;
}

// Concrete response type for your JSON
export type CreateRubricResponse = {
  isSuccess: boolean;
  message: string;
  data: RubricItem[];
};

export interface CheckpointItem {
  id: string;
  metadata: {};
  project_id: string;
  title: string;
  description: string;
  checkpoint_number: number;
  due_date: string;
  weight_percentage: string;
  is_required: boolean;
  status: string;
  created_by: string;
  updatedAt: string; // ISO timestamp string
  createdAt: string; // ISO timestamp string
  deletedAt: string | null;
}

export interface CreateCheckPointResponse {
  isSuccess: boolean;
  message: string;
  data: CheckpointItem[];
}

export interface Course {
  id: string;
  name: string;
  code: string;
  term: string;
  academicYear: string;
  startDate: string;
  endDate: string;
}

export interface CourseResponse {
  isSuccess: boolean;
  message: string;
  data: Course[];
}

interface CourseTeachingStaffResponse {
  isSuccess: boolean;
  message: string;
  data: CourseStaff;
}

export interface CourseStaff {
  instructors: StaffMember[];
  tas: StaffMember[];
}

interface StaffMember {
  id: string;
  name: string;
  email: string;
  profilePicture: string;
  role: typeof ROLES.INSTRUCTOR | typeof ROLES.TA;
  enrolledAt: string;
}
type SaveAsTemplateResponse = CreateProjectResponse;
type CreateTemplateResponse = CreateProjectResponse;
// Create Project
export async function createProject(
  projectData: ProjectData,
): Promise<CreateProjectResponse> {
  const url = getBITSEndApiMap("createProject");
  const { data } = await api.post<CreateProjectResponse>(url, projectData);
  console.log("create project response", data);
  return data;
}
export async function editProject(
  projectId: string,
  projectData: ProjectData,
): Promise<CreateProjectResponse> {
  const baseUrl = getBITSEndApiMap("editProject");
  console.log(baseUrl);
  const url = `${baseUrl}/${projectId}/enhanced`;

  const { data } = await api.put<CreateProjectResponse>(url, projectData);
  console.log("edit project response", data);
  return data;
}

//Upload dataset
export async function uploadDataset(
  fd: FormData,
): Promise<UploadProjectDatasetResponse> {
  const url = getBITSEndApiMap("uploadDataset");
  const { data } = await api.post<UploadProjectDatasetResponse>(url, fd);
  console.log("upload dataset response", data);
  return data;
}

//Create Checkpoint
export async function createCheckPoint(
  checkPointsData: checkpoints[],
): Promise<CreateCheckPointResponse> {
  const url = getBITSEndApiMap("createCheckPoint");
  const { data } = await api.post<CreateCheckPointResponse>(
    url,
    checkPointsData,
  );
  console.log("create checkpoint response", data);
  return data;
}

export function useUpdateCheckPointsMutation() {
  return useMutation({
    mutationFn: updateCheckPoints,
  });
}

interface UpdateCheckpointsPayload {
  projectId?: string;
  project_id?: string;
  checkpoints: checkpoints[];
}

export async function updateCheckPoints(
  checkPointsData: UpdateCheckpointsPayload,
): Promise<CreateCheckPointResponse> {
  const bulkUpdateUrl = getBITSEndApiMap("bulkUpdateCheckPoints");
  const { data } = await api.put<CreateCheckPointResponse>(
    bulkUpdateUrl,
    checkPointsData,
  );
  console.log("bulk update checkpoint response", data);
  return data;
}

export function useRemoveDatasetMutation() {
  return useMutation({
    mutationFn: removeDataset,
  });
}

export async function removeDataset(params: {
  datasetId: string;
  projectId: string;
}): Promise<any> {
  const url = getBITSEndApiMap("uploadDataset");

  const formData = new FormData();
  formData.append("isUploadFils", "false"); // must be string in FormData
  formData.append("projectId", params.projectId);

  // ✅ handle array properly
  [params.datasetId].forEach((id) => {
    formData.append("filesToDelete[]", id);
  });

  console.log("formData being sent", {
    isUploadFiles: "false",
    projectId: params.projectId,
    filesToDelete: [params.datasetId],
  });

  const { data } = await api.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  console.log("remove dataset response", data);
  return data;
}
export async function createTemplateWithProjectId(
  projectId: string,
  body: {
    title: string;
    description: string;
    category: string;
    subcategory: string;
    skills_covered: string[];
    technologies_used: string[];
  },
): Promise<CreateTemplateResponse> {
  const url = getBITSEndApiMap("createTemplate"); // should map to /api/projects/templates
  const payload = { projectId, ...body };

  const { data } = await api.post<CreateTemplateResponse>(url, payload);
  console.log("create template response", data);
  return data;
}

// Convert project to template using specific endpoint
export async function convertProjectToTemplate(
  projectId: string,
  body: {
    title: string;
    description: string;
    category: string;
    subcategory: string;
    skills_covered: string[];
    technologies_used: string[];
  },
): Promise<CreateTemplateResponse> {
  const baseUrl = getBITSEndApiMap("convertProjectToTemplate");
  const url = buildUrl(baseUrl, { projectId });

  const { data } = await api.post<CreateTemplateResponse>(url, body);
  console.log("convert project to template response", data);
  return data;
}

export function useCreateTemplateWithProjectIdMutation() {
  return useMutation({
    mutationFn: ({
      projectId,
      body,
    }: {
      projectId: string;
      body: {
        title: string;
        description: string;
        category: string;
        subcategory: string;
        skills_covered: string[];
        technologies_used: string[];
      };
    }) => createTemplateWithProjectId(projectId, body),
  });
}

export function useConvertProjectToTemplateMutation() {
  return useMutation({
    mutationFn: ({
      projectId,
      body,
    }: {
      projectId: string;
      body: {
        title: string;
        description: string;
        category: string;
        subcategory: string;
        skills_covered: string[];
        technologies_used: string[];
      };
    }) => convertProjectToTemplate(projectId, body),
  });
}

//Create rubric
export async function createRubric(
  rubricData: GradingRubric[],
): Promise<CreateRubricResponse> {
  const url = getBITSEndApiMap("createRubric");
  const { data } = await api.post<CreateRubricResponse>(url, rubricData);
  console.log("create rubric response", data);
  return data;
}

export async function getCourses(): Promise<Course[]> {
  const url = getBITSEndApiMap("getCourses");
  const { data } = await api.get<CourseResponse>(url);
  return data.data;
}

export async function getInstructorsAndTeachingAssistants(
  courseId: string,
): Promise<CourseStaff> {
  const url = getBITSEndApiMap("getCourseTeachingStaff").replace(
    ":courseId",
    courseId,
  );
  const { data } = await api.get<CourseTeachingStaffResponse>(url);
  console.log("API called");
  return data.data;
}

// Generic function for sending FormData to any endpoint
export async function sendFormData<T = any>(
  endpoint: keyof typeof import("../apiMap").default extends (
    arg: infer K,
  ) => any
    ? K
    : string,
  formData: FormData,
  pathParams?: Record<string, string | number>,
): Promise<T> {
  let url = getBITSEndApiMap(endpoint as any);

  // Replace path parameters if provided
  if (pathParams) {
    url = buildUrl(url, pathParams);
  }

  const { data } = await api.post<T>(url, formData);
  console.log(`${endpoint} response`, data);
  return data;
}

// Checkpoint Management Hooks
export function useCoursesQuery(
  enabled = true,
): UseQueryResult<Course[], Error> {
  return useQuery<Course[], Error>({
    queryKey: ["courses"],
    queryFn: getCourses, // getCourses() already returns Promise<Course[]>
    enabled,
    staleTime: 60_000,
  });
}

export function useStaffMemberQuery(
  courseId: string,
  enabled = true,
): UseQueryResult<CourseStaff> {
  return useQuery({
    queryKey: ["courseStaff", courseId] as QueryKey,
    queryFn: () => getInstructorsAndTeachingAssistants(courseId),
    enabled: enabled && !!courseId,
    staleTime: 60_000,
  });
}

export function useCreateProjectMutation() {
  return useMutation({
    mutationFn: createProject,
  });
}
export function useEditProjectMutation() {
  return useMutation({
    mutationFn: ({
      projectId,
      projectData,
    }: {
      projectId: string;
      projectData: ProjectData;
    }) => editProject(projectId, projectData),
  });
}
export function useDatasetMutation() {
  return useMutation({
    mutationFn: (fd: FormData) => uploadDataset(fd),
  });
}

export function useCreateCheckPointMutation() {
  return useMutation({
    mutationFn: createCheckPoint,
  });
}

export function useCreateRubricMutation() {
  return useMutation({
    mutationFn: createRubric,
  });
}
export async function saveAsTemplate(
  projectData: ProjectData,
): Promise<SaveAsTemplateResponse> {
  const url = getBITSEndApiMap("saveAsTemplate");
  const { data } = await api.post<SaveAsTemplateResponse>(url, projectData);
  console.log("save as template response", data);
  return data;
}

export function useSaveAsTemplateMutation() {
  return useMutation({
    mutationFn: saveAsTemplate,
  });
}
export async function updateTemplate(
  projectData: ProjectData,
): Promise<SaveAsTemplateResponse> {
  if (!projectData.id) throw new Error("Project ID is required for update");

  const url = getBITSEndApiMap("updateTemplate").replace(
    ":templateId",
    projectData.id,
  );

  const { id, ...payload } = projectData;

  const { data } = await api.put<SaveAsTemplateResponse>(url, payload);
  console.log("save as template response", data);
  return data;
}

export function useUpdateTemplateMutation() {
  return useMutation({
    mutationFn: updateTemplate,
  });
}

// Generic hook for FormData mutations
//Usage:
// For a specific endpoint
// const formDataMutation = useFormDataMutation('someEndpoint');

// // For endpoints with path parameters
// const formDataMutation = useFormDataMutation('someEndpoint', { projectId: '123' });

// // Usage
// const formData = new FormData();
// formData.append('data', 'value');
// formDataMutation.mutate(formData);
export function useFormDataMutation<T = any>(
  endpoint: string,
  pathParams?: Record<string, string | number>,
) {
  return useMutation({
    mutationFn: (formData: FormData) =>
      sendFormData<T>(endpoint, formData, pathParams),
  });
}
// 🔹 API function
export interface BulkUpdateRubricPayload {
  project_id: string;
  rubrics: GradingRubric[];
  is_template?: boolean;
  template_name?: string;
}

export async function updateRubric(
  payload: BulkUpdateRubricPayload,
): Promise<CreateRubricResponse> {
  if (!payload?.project_id) {
    throw new Error("project_id is required to update rubrics");
  }

  const url = getBITSEndApiMap("updateRubric");
  const { data } = await api.put<CreateRubricResponse>(url, payload);
  console.log("update rubric response", data);
  return data;
}

// 🔹 React Query mutation hook
export function useUpdateRubricMutation() {
  return useMutation({
    mutationFn: updateRubric,
  });
}

export async function createProjectFromTemplate(
  templateData: TemplateData,
): Promise<CreateProjectResponse> {
  const url = getBITSEndApiMap("createProjectFromTemplate");
  const { data } = await api.post<CreateProjectResponse>(url, templateData);
  console.log("create project from template response", data);
  return data;
}

export function useCreateProjectFromTemplateMutation() {
  return useMutation({
    mutationFn: createProjectFromTemplate,
  });
}

export async function updateProject(
  payload: UpdateProjectPayload,
): Promise<UpdateProjectResponse> {
  const baseUrl = getBITSEndApiMap("getTemplates");
  const url = `${baseUrl}/${payload.projectId}`;

  const { data } = await api.put<UpdateProjectResponse>(url, payload);
  console.log("update project response", data);

  return data;
}

// Mutation Hook
export function useUpdateProjectMutation() {
  return useMutation({
    mutationFn: (payload: UpdateProjectPayload) => updateProject(payload),
  });
}

// Resource Profile Dropdown API
export interface ResourceProfileDropdownItem {
  id: string;
  name: string;
  [key: string]: any;
}

export async function getResourceProfilesDropdown(): Promise<
  ResourceProfileDropdownItem[]
> {
  const url = getBITSEndApiMap("getResourceProfilesDropdown");
  const { data } = await api.get<{ data: ResourceProfileDropdownItem[] }>(url);
  return data.data;
}

export function useResourceProfilesDropdownQuery(
  options?: Partial<UseQueryOptions<ResourceProfileDropdownItem[], Error>>,
) {
  return useQuery<ResourceProfileDropdownItem[], Error>({
    queryKey: ["resourceProfilesDropdown"],
    queryFn: () => getResourceProfilesDropdown(),
    ...(options as any),
  });
}

// Checkpoint Action API
export async function checkpointAction(params: {
  userId: string;
  projectId: string;
  checkpointId: string;
}): Promise<any> {
  const url = getBITSEndApiMap("checkpointAction")
    .replace(":userId", params.userId)
    .replace(":projectId", params.projectId);

  const { data } = await api.get(url, {
    params: { checkpointId: params.checkpointId },
  });
  console.log("checkpoint action response", data);
  return data;
}

export function useCheckpointAction() {
  return useMutation({
    mutationFn: checkpointAction,
  });
}
